/**
 * When the person last did something the server counts (NF-008): the moment
 * of the last request without `X-Background`. The idle limit runs from there,
 * so the expiry warning computes its idle edge from this.
 *
 * Shared between tabs through localStorage: another tab's request resets the
 * same session, and a tab that ignored it would warn a person who is working.
 * Only a timestamp is stored - no secret, nothing that opens anything - and a
 * storage that refuses (private window, blocked site data) leaves the
 * in-memory value, which is right for this tab.
 *
 * React-free, so the api-client can write it without an import cycle.
 */

const STORAGE_KEY = "incident.last-activity";

let lastActivity: number | null = null;
const listeners = new Set<() => void>();

function readStored(): number | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

/** The latest activity this browser knows of, in epoch milliseconds. */
export function lastActivityAt(): number | null {
  const stored = typeof window === "undefined" ? null : readStored();
  if (stored === null) return lastActivity;
  if (lastActivity === null) return stored;
  return Math.max(stored, lastActivity);
}

/** Record a request the server counted as activity. */
export function noteActivity(at: number): void {
  lastActivity = lastActivity === null ? at : Math.max(lastActivity, at);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(lastActivity));
    } catch {
      /* the in-memory value stands */
    }
  }
  for (const listener of listeners) listener();
}

/** Follow activity, this tab's and (via the storage event) the others'. */
export function subscribeActivity(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY) listener();
  };
  if (typeof window !== "undefined") window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== "undefined") window.removeEventListener("storage", onStorage);
  };
}

/** Forget - after a logout, so the next person's warning starts clean. */
export function resetActivity(): void {
  lastActivity = null;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing stored, nothing to remove */
    }
  }
}
